import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import ReactSelect from 'react-select';
import IcomoonReact from 'icomoon-react';
import SharedModal from '../../../shared/_TBComponents/Modal';
import Button from '../../../shared/_TBComponents/Button';
import iconSet from '../../../shared/images/teambuildr-selection.json';
import {
  formatWeekDisplay,
  addWeeks,
  getCurrentWeek,
  compareWeeks,
} from '../utils/weekUtils';

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
  font-family: 'Nunito Sans', sans-serif;
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const ModalTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  color: #444444;
  margin: 0;
  letter-spacing: -0.24px;
  line-height: normal;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9e9e9e;
  flex-shrink: 0;

  &:hover {
    opacity: 0.7;
  }
`;

const ModalDescription = styled.p`
  font-size: 16px;
  font-weight: 400;
  color: #9e9e9e;
  margin: 0;
  line-height: 1.5;
`;

const SelectGroup = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
  height: 66px;
  align-items: center;
`;

const SelectWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Label = styled.label`
  font-size: 16px;
  font-weight: 700;
  color: #444444;
  display: block;
  line-height: 1.5;
`;

const SelectContainer = styled.div`
  width: 100%;

  .react-select__control {
    border: 2px solid #e0e0e0;
    border-radius: 4px;
    min-height: 40px;
    font-family: 'Nunito Sans', sans-serif;
    box-shadow: none;
    cursor: pointer;
    background: #ffffff;

    &:hover {
      border-color: #b0b0b0;
    }
  }

  .react-select__control--is-focused {
    border-color: #e0e0e0;
    box-shadow: none;
  }

  .react-select__value-container {
    padding: 8px 16px;
  }

  .react-select__single-value {
    font-size: 16px;
    font-weight: 400;
    color: #444444;
    line-height: 1.5;
  }

  .react-select__placeholder {
    font-size: 16px;
    color: #9e9e9e;
  }

  .react-select__indicator-separator {
    display: none;
  }

  .react-select__dropdown-indicator {
    color: #9e9e9e;
    padding: 8px;

    &:hover {
      color: #444444;
    }
  }

  .react-select__menu {
    border-radius: 4px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    overflow: hidden;
    margin-top: 4px;
    z-index: 9999;
  }

  .react-select__menu-list {
    padding: 0;
    max-height: 300px;
  }

  .react-select__option {
    font-size: 16px;
    font-weight: 400;
    padding: 10px 16px;
    cursor: pointer;
    font-family: 'Nunito Sans', sans-serif;
  }

  .react-select__option--is-focused {
    background-color: #f5f5f5;
  }

  .react-select__option--is-selected {
    background-color: #444444;
    color: #ffffff;
    font-weight: 600;

    &:hover {
      background-color: #444444;
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 4px;
  width: 100%;
`;

/**
 * Generate a list of weeks for the "from" dropdown
 * Starts from 52 weeks in the past and goes 52 weeks into the future (2 years total)
 */
const generateFromWeekOptions = () => {
  const currentWeek = getCurrentWeek();
  const weeks = [];

  // Generate weeks: 52 weeks in the past to 52 weeks in the future
  for (let i = -52; i <= 52; i += 1) {
    const week = addWeeks(currentWeek, i);
    weeks.push({
      value: week,
      label: formatWeekDisplay(week, 'medium'), // e.g., "Sep 25-Oct 6, 2025"
    });
  }

  return weeks;
};

/**
 * Generate a list of weeks for the "to" dropdown based on selected "from" week
 * Only includes weeks that are equal to or after the "from" week
 */
const generateToWeekOptions = (fromWeek) => {
  if (!fromWeek) {
    return generateFromWeekOptions(); // If no from week selected, show all weeks
  }

  const currentWeek = getCurrentWeek();
  const weeks = [];

  // Generate weeks from 52 weeks in the past to 52 weeks in the future
  for (let i = -52; i <= 52; i += 1) {
    const week = addWeeks(currentWeek, i);
    // Only include weeks that are >= fromWeek
    if (compareWeeks(week, fromWeek) >= 0) {
      weeks.push({
        value: week,
        label: formatWeekDisplay(week, 'medium'),
      });
    }
  }

  return weeks;
};

/**
 * ExportModal Component
 *
 * Modal for selecting a week range (from/to) before exporting the planner to PDF.
 * Uses react-select dropdowns with improved styling and smart filtering.
 */
const ExportModal = ({
  defaultFromWeek, defaultToWeek, onClose, onExport,
}) => {
  const currentWeek = getCurrentWeek();
  const [selectedFromWeek, setSelectedFromWeek] = useState(
    defaultFromWeek || currentWeek,
  );
  const [selectedToWeek, setSelectedToWeek] = useState(
    defaultToWeek || addWeeks(currentWeek, 11),
  );

  // Generate options for "from" dropdown
  const fromWeekOptions = useMemo(() => generateFromWeekOptions(), []);

  // Generate options for "to" dropdown (based on selected "from" week)
  const toWeekOptions = useMemo(() => generateToWeekOptions(selectedFromWeek), [
    selectedFromWeek,
  ]);

  // Find the selected option objects for react-select
  // Build fallback options if the selected week is outside the generated range
  const selectedFromOption = fromWeekOptions.find(
    (opt) => opt.value === selectedFromWeek,
  ) || (selectedFromWeek ? {
    value: selectedFromWeek,
    label: formatWeekDisplay(selectedFromWeek, 'medium'),
  } : null);

  const selectedToOption = toWeekOptions.find(
    (opt) => opt.value === selectedToWeek,
  ) || (selectedToWeek ? {
    value: selectedToWeek,
    label: formatWeekDisplay(selectedToWeek, 'medium'),
  } : null);

  const handleFromChange = (option) => {
    const newFromWeek = option.value;
    setSelectedFromWeek(newFromWeek);

    // If the new "from" week is after the current "to" week, adjust "to" week
    if (compareWeeks(newFromWeek, selectedToWeek) > 0) {
      setSelectedToWeek(newFromWeek);
    }
  };

  const handleToChange = (option) => {
    setSelectedToWeek(option.value);
  };

  const handleExport = () => {
    onExport(selectedFromWeek, selectedToWeek);
  };

  return (
    <SharedModal
      isOpen
      onRequestClose={onClose}
      customButton
      hideTitle
      customMinHeight='auto'
      customMaxWidth='600px'
      customPadding='24px'
    >
      <ModalContent>
        <HeaderSection>
          <TitleSection>
            <TitleRow>
              <ModalTitle>Export to PDF</ModalTitle>
              <CloseButton onClick={onClose} type='button' aria-label='Close'>
                <IcomoonReact iconSet={iconSet} size={16} icon='remove' color='#9e9e9e' />
              </CloseButton>
            </TitleRow>
            <ModalDescription>
              Select the date range you want to export. The planner will be
              exported in review mode with all blocks and notes.
            </ModalDescription>
          </TitleSection>

          <SelectGroup>
            <SelectWrapper>
              <Label htmlFor='export-from-week-select'>Start Week</Label>
              <SelectContainer>
                <ReactSelect
                  inputId='export-from-week-select'
                  value={selectedFromOption}
                  onChange={handleFromChange}
                  options={fromWeekOptions}
                  classNamePrefix='react-select'
                  isSearchable={false}
                  placeholder='Select start week'
                  menuPlacement='auto'
                />
              </SelectContainer>
            </SelectWrapper>

            <SelectWrapper>
              <Label htmlFor='export-to-week-select'>End Week</Label>
              <SelectContainer>
                <ReactSelect
                  inputId='export-to-week-select'
                  value={selectedToOption}
                  onChange={handleToChange}
                  options={toWeekOptions}
                  classNamePrefix='react-select'
                  isSearchable={false}
                  placeholder='Select end week'
                  menuPlacement='auto'
                />
              </SelectContainer>
            </SelectWrapper>
          </SelectGroup>
        </HeaderSection>

        <ButtonGroup>
          <Button
            name='Export'
            onClick={handleExport}
            bgColor='#444444'
            txtColor='#FFFFFF'
            noBorder
            upperCase
            fontSize='16px'
            customBorderRadius='4px'
            styledWrapperProps={{
              flex: 1,
              height: '36px',
              padding: '6px 16px',
              fontWeight: 700,
              letterSpacing: '0.48px',
              lineHeight: 1.5,
            }}
            styledTextProps={{
              fontWeight: 700,
            }}
          />
          <Button
            name='Cancel'
            onClick={onClose}
            upperCase
            fontSize='16px'
            customBorderRadius='4px'
            styledWrapperProps={{
              flex: 1,
              height: '36px',
              padding: '6px 16px',
              background: 'transparent',
              border: '2px solid #444444',
              fontWeight: 700,
              letterSpacing: '0.48px',
              lineHeight: 1.5,
            }}
            styledTextProps={{
              fontWeight: 700,
            }}
          />
        </ButtonGroup>
      </ModalContent>
    </SharedModal>
  );
};

ExportModal.propTypes = {
  defaultFromWeek: PropTypes.string,
  defaultToWeek: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onExport: PropTypes.func.isRequired,
};

ExportModal.defaultProps = {
  defaultFromWeek: null,
  defaultToWeek: null,
};

export default ExportModal;
